/**
 * RFC 4180-compliant CSV parser and template generator.
 * Handles quoted fields, commas in values, escaped quotes, multi-line fields, and BOM.
 */

// FEAT-2026-0017: "Location ID" (borehole / test pit id) is an OPTIONAL first
// column so one file can carry samples for many locations. Blank = the location
// selected in the side panel; unknown ids are created on import.
const CSV_TEMPLATE_HEADERS = [
  'Location ID', 'Sample Reference', 'Depth Top (m)', 'Depth Base (m)', 'Type', 'Sample Status',
  'Priority', 'Condition', 'Comments', 'Shipment Box/Bucket', 'Storage Location',
] as const;

export type CsvHeaders = typeof CSV_TEMPLATE_HEADERS;

export interface CsvParseResult {
  headers: string[];
  rows: Record<string, string>[];
  rawRowNumbers: number[]; // original line numbers (1-based) for each row
}

/** Strip UTF-8 BOM if present */
const stripBom = (text: string): string =>
  text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;

/**
 * Parse a CSV string respecting RFC 4180:
 * - Quoted fields may contain commas, newlines, and escaped quotes ("")
 * - Trims whitespace from unquoted fields
 */
export function parseCsv(text: string): CsvParseResult {
  const cleaned = stripBom(text);
  const rows: string[][] = [];
  const rawRowNumbers: number[] = [];
  let current: string[] = [];
  let field = '';
  let inQuotes = false;
  let lineNum = 1;
  let fieldStartLine = 1;

  for (let i = 0; i < cleaned.length; i++) {
    const ch = cleaned[i];
    const next = cleaned[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        field += '"';
        i++; // skip escaped quote
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        if (ch === '\n') lineNum++;
        field += ch;
      }
    } else {
      if (ch === '"' && field.length === 0) {
        inQuotes = true;
      } else if (ch === ',') {
        current.push(field.trim());
        field = '';
      } else if (ch === '\r' && next === '\n') {
        // CRLF
        current.push(field.trim());
        if (current.some((f) => f !== '')) {
          rows.push(current);
          rawRowNumbers.push(fieldStartLine);
        }
        current = [];
        field = '';
        lineNum++;
        fieldStartLine = lineNum;
        i++; // skip \n
      } else if (ch === '\n') {
        current.push(field.trim());
        if (current.some((f) => f !== '')) {
          rows.push(current);
          rawRowNumbers.push(fieldStartLine);
        }
        current = [];
        field = '';
        lineNum++;
        fieldStartLine = lineNum;
      } else {
        field += ch;
      }
    }
  }

  // Last field/row
  if (field || current.length > 0) {
    current.push(field.trim());
    if (current.some((f) => f !== '')) {
      rows.push(current);
      rawRowNumbers.push(fieldStartLine);
    }
  }

  if (rows.length === 0) {
    return { headers: [], rows: [], rawRowNumbers: [] };
  }

  const headers = rows[0];
  const dataRows: Record<string, string>[] = [];
  const dataRowNumbers: number[] = [];

  for (let i = 1; i < rows.length; i++) {
    const row: Record<string, string> = {};
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = rows[i][j] ?? '';
    }
    dataRows.push(row);
    dataRowNumbers.push(rawRowNumbers[i]);
  }

  return { headers, rows: dataRows, rawRowNumbers: dataRowNumbers };
}

/** Trigger a browser download of the blank CSV template */
export function downloadCsvTemplate(): void {
  const csv = CSV_TEMPLATE_HEADERS.join(',') + '\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'sample-upload-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export { CSV_TEMPLATE_HEADERS };
